import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { 
  Plus, Users, Trash2, ChevronDown, ChevronUp, UserPlus, UserMinus,
  Lightbulb, Wrench, Search, Heart, Target, Megaphone, AlertTriangle, CheckCircle, Loader2
} from 'lucide-react';

const MinistryManager = ({ allUsers }) => {
  const [ministries, setMinistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedMinistry, setExpandedMinistry] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedMinistry, setSelectedMinistry] = useState(null);
  const [newMinistry, setNewMinistry] = useState({ name: '', description: '', ideal_composition: {} });

  const dimensionInfo = {
    B: { name: 'Brainstorm', icon: Lightbulb, color: 'bg-purple-500', lightBg: 'bg-purple-100', text: 'text-purple-700' },
    U: { name: 'Uncover', icon: Wrench, color: 'bg-blue-500', lightBg: 'bg-blue-100', text: 'text-blue-700' },
    I: { name: 'Inspect', icon: Search, color: 'bg-green-500', lightBg: 'bg-green-100', text: 'text-green-700' },
    L: { name: 'Lift', icon: Heart, color: 'bg-pink-500', lightBg: 'bg-pink-100', text: 'text-pink-700' },
    D: { name: 'Deliver', icon: Target, color: 'bg-orange-500', lightBg: 'bg-orange-100', text: 'text-orange-700' },
    R: { name: 'Rally', icon: Megaphone, color: 'bg-red-500', lightBg: 'bg-red-100', text: 'text-red-700' }
  };

  useEffect(() => {
    fetchMinistries();
  }, []);

  const fetchMinistries = async () => {
    try {
      const response = await axios.get(`${API}/admin/ministries`);
      setMinistries(response.data.ministries || []);
    } catch (error) {
      toast.error('Failed to load ministries');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMinistry = async () => {
    if (!newMinistry.name.trim()) {
      toast.error('Ministry name is required');
      return;
    }

    try {
      await axios.post(`${API}/admin/ministries`, newMinistry);
      toast.success('Ministry created successfully');
      setShowCreateDialog(false);
      setNewMinistry({ name: '', description: '', ideal_composition: {} });
      fetchMinistries();
    } catch (error) {
      toast.error('Failed to create ministry');
    }
  };

  const handleDeleteMinistry = async (ministryId) => {
    if (!window.confirm('Are you sure you want to delete this ministry? All member assignments will be removed.')) {
      return;
    }

    try {
      await axios.delete(`${API}/admin/ministries/${ministryId}`);
      toast.success('Ministry deleted');
      fetchMinistries();
    } catch (error) {
      toast.error('Failed to delete ministry');
    }
  };

  const handleAssignUser = async (userId, role = 'Member') => {
    try {
      await axios.post(`${API}/admin/ministries/assign`, {
        user_id: userId,
        ministry_id: selectedMinistry.id,
        role_in_ministry: role
      });
      toast.success('User assigned to ministry');
      setShowAssignDialog(false);
      fetchMinistries();
    } catch (error) {
      toast.error('Failed to assign user');
    }
  };

  const handleRemoveUser = async (ministryId, userId) => {
    try {
      await axios.delete(`${API}/admin/ministries/${ministryId}/users/${userId}`);
      toast.success('User removed from ministry');
      fetchMinistries();
    } catch (error) {
      toast.error('Failed to remove user');
    }
  };

  // Get users not in selected ministry
  const getAvailableUsers = () => {
    if (!selectedMinistry) return allUsers;
    const memberIds = selectedMinistry.members.map(m => m.user_id);
    return allUsers.filter(u => !memberIds.includes(u.user?.id));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600 mx-auto" />
          <p className="mt-2 text-gray-500">Loading ministries...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold">Ministry & Team Management</h2>
          <p className="text-gray-500 text-sm">Create ministries and assign members to build balanced teams</p>
        </div>
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-indigo-600 hover:bg-indigo-700">
              <Plus className="h-4 w-4 mr-2" />
              Create Ministry
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Ministry</DialogTitle>
              <DialogDescription>
                Define a ministry or team and set ideal composition goals
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Ministry Name</Label>
                <Input
                  value={newMinistry.name}
                  onChange={(e) => setNewMinistry({ ...newMinistry, name: e.target.value })}
                  placeholder="e.g., Worship Team, Kids Ministry"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={newMinistry.description}
                  onChange={(e) => setNewMinistry({ ...newMinistry, description: e.target.value })}
                  placeholder="Brief description of this ministry..."
                  rows={2}
                />
              </div>
              <div>
                <Label className="mb-2 block">Ideal Composition (Optional)</Label>
                <p className="text-xs text-gray-500 mb-3">Set target number for each strength type</p>
                <div className="grid grid-cols-3 gap-2">
                  {Object.entries(dimensionInfo).map(([dim, info]) => (
                    <div key={dim} className="flex items-center gap-2">
                      <div className={`p-1 rounded ${info.lightBg}`}>
                        <info.icon className={`h-4 w-4 ${info.text}`} />
                      </div>
                      <Input
                        type="number"
                        min="0"
                        className="w-16 h-8"
                        placeholder="0"
                        value={newMinistry.ideal_composition[dim] || ''}
                        onChange={(e) => setNewMinistry({
                          ...newMinistry,
                          ideal_composition: {
                            ...newMinistry.ideal_composition,
                            [dim]: parseInt(e.target.value) || 0
                          }
                        })}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <Button onClick={handleCreateMinistry} className="w-full">
                Create Ministry
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Ministries List */}
      {ministries.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No Ministries Yet</h3>
            <p className="text-gray-500 mb-4">Create your first ministry to start building teams</p>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create Ministry
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {ministries.map((ministry) => {
            const isExpanded = expandedMinistry === ministry.id;
            const hasGaps = Object.keys(ministry.ideal_composition || {}).length > 0 && 
              Object.entries(ministry.ideal_composition).some(([dim, needed]) => 
                (ministry.composition[dim] || 0) < needed
              );
            
            return (
              <Card key={ministry.id} className={hasGaps ? 'border-amber-200' : ''}>
                <CardHeader 
                  className="cursor-pointer"
                  onClick={() => setExpandedMinistry(isExpanded ? null : ministry.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                        <Users className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          {ministry.name}
                          {hasGaps && (
                            <Badge variant="outline" className="text-amber-600 border-amber-300">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              Gaps
                            </Badge>
                          )}
                        </CardTitle>
                        <CardDescription>{ministry.member_count} members</CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Mini composition preview */}
                      <div className="hidden md:flex gap-1">
                        {Object.entries(ministry.composition).map(([dim, count]) => {
                          if (count === 0) return null;
                          const info = dimensionInfo[dim];
                          return (
                            <div key={dim} className={`px-2 py-1 rounded text-xs ${info.lightBg} ${info.text}`}>
                              {dim}: {count}
                            </div>
                          );
                        })}
                      </div>
                      {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                    </div>
                  </div>
                </CardHeader>

                {isExpanded && (
                  <CardContent className="border-t pt-4">
                    {ministry.description && (
                      <p className="text-gray-600 mb-4">{ministry.description}</p>
                    )}

                    {/* Composition vs Ideal */}
                    {Object.keys(ministry.ideal_composition || {}).length > 0 && (
                      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-medium mb-3">Team Composition vs Ideal</h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                          {Object.entries(ministry.ideal_composition).map(([dim, needed]) => {
                            const current = ministry.composition[dim] || 0;
                            const info = dimensionInfo[dim];
                            const Icon = info.icon;
                            const isMet = current >= needed;
                            
                            return (
                              <div key={dim} className="flex items-center gap-2">
                                <div className={`p-1 rounded ${info.lightBg}`}>
                                  <Icon className={`h-4 w-4 ${info.text}`} />
                                </div>
                                <span className="text-sm">
                                  {current}/{needed}
                                </span>
                                {isMet ? (
                                  <CheckCircle className="h-4 w-4 text-green-500" />
                                ) : (
                                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Members List */}
                    <div className="mb-4">
                      <div className="flex justify-between items-center mb-3">
                        <h4 className="font-medium">Members</h4>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => {
                            setSelectedMinistry(ministry);
                            setShowAssignDialog(true);
                          }}
                        >
                          <UserPlus className="h-4 w-4 mr-1" />
                          Add Member
                        </Button>
                      </div>
                      
                      {ministry.members.length === 0 ? (
                        <p className="text-gray-500 text-sm py-4 text-center">No members assigned yet</p>
                      ) : (
                        <div className="space-y-2">
                          {ministry.members.map((member) => {
                            const info = member.primary_dimension ? dimensionInfo[member.primary_dimension] : null;
                            return (
                              <div key={member.user_id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                                <div className="flex items-center gap-3">
                                  {info && (
                                    <div className={`p-1 rounded ${info.lightBg}`}>
                                      <info.icon className={`h-4 w-4 ${info.text}`} />
                                    </div>
                                  )}
                                  <div>
                                    <p className="font-medium text-sm">{member.name}</p>
                                    <p className="text-xs text-gray-500">
                                      {member.pairing_code || 'Not assessed'} • {member.role_in_ministry}
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-600 hover:text-red-700"
                                  onClick={() => handleRemoveUser(ministry.id, member.user_id)}
                                >
                                  <UserMinus className="h-4 w-4" />
                                </Button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end pt-4 border-t">
                      <Button
                        variant="outline"
                        className="text-red-600 border-red-300 hover:bg-red-50"
                        onClick={() => handleDeleteMinistry(ministry.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete Ministry
                      </Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* Assign User Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Member to {selectedMinistry?.name}</DialogTitle>
            <DialogDescription>
              Select a user to add to this ministry
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-2 mt-4">
            {getAvailableUsers().filter(u => u.assessment_result).map((user) => {
              const dim = user.assessment_result?.primary_dimension;
              const info = dim ? dimensionInfo[dim] : null;
              
              return (
                <div 
                  key={user.user?.id} 
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleAssignUser(user.user?.id)}
                >
                  <div className="flex items-center gap-3">
                    {info && (
                      <div className={`p-2 rounded ${info.lightBg}`}>
                        <info.icon className={`h-4 w-4 ${info.text}`} />
                      </div>
                    )}
                    <div>
                      <p className="font-medium">{user.user?.name}</p>
                      <p className="text-sm text-gray-500">
                        {user.assessment_result?.pairing_code || 'Not assessed'}
                      </p>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              );
            })}
            {getAvailableUsers().filter(u => u.assessment_result).length === 0 && (
              <p className="text-center text-gray-500 py-8">
                No available users with assessment results
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MinistryManager;
